/**
 * Custom hook for initializing the lobby
 * Handles fetching settings and updating the app store
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { HOT_AND_COLD_SECTION_NAME, LAST_PLAYED_SECTION_NAME, SECTION_NAMES } from '@repo/common';

import { useFavoritesStore } from 'features/favorites';
import { useJackpotsStore } from 'features/jackpots';
import { useCampaignActions } from 'features/campaigns';
import { useTopGames, useTrendingGames } from 'features/games';

import { fetchSettings } from '../../core/api/endpoints/settings.api';
import { fetchHotColdStats } from '../../core/api/endpoints/hotCold.api';
import { applyTrendingSection } from '../../features/games/services/trendingGamesService';
import { useIdleManager } from '../../core/services/managers/IdleManager';
import TranslationService from '../../core/services/i18n/TranslationService';
import { sessionService } from '../../core/services/SessionService';
import { dynamicSectionsTracker } from '../../core/services/DynamicSectionsTracker';
import { lastPlayedService } from '../../features/lastPlayed/services/lastPlayedService';
import { useAppStore, useSettings, useBalance, useAssetsConfig, useFetchCurrentPlayers, useIsIdle } from '../app/appStore';
import { useFeatureFlagsStore } from '../featureFlags/featureFlagsStore';
import { soundManager } from '../../core/services/managers/SoundManager';
import { createHotColdSection } from 'features/hotCold';
import type { IHotColdStatsResponse } from 'features/hotCold';
import type ISectionSettings from 'types/models/ISectionSettings';
import { buildPortalAssetUrl } from 'shared/utils/helpers';
import type { IContextSectionSettings } from 'types/models/ISectionSettings';

interface UseInitializeLobbyOptions {
  enabled?: boolean;
}

export const useInitializeLobby = (options: UseInitializeLobbyOptions = {}) => {
  const { enabled = true } = options;

  // Get current settings and balance from store
  const settings = useSettings();
  const balance = useBalance();
  const assetsConfig = useAssetsConfig();
  const shouldInitialize = useAppStore((state) => state.shouldInitialize);
  const isIdle = useIsIdle();

  // Use IdleManager for process management
  const { registerProcess, unregisterProcess } = useIdleManager();

  // Store actions
  const setSections = useAppStore((state) => state.setSections);
  const setSupportedGames = useAppStore((state) => state.setSupportedGames);
  const updateSettings = useAppStore((state) => state.updateSettings);
  const setSupportedLanguages = useAppStore((state) => state.setSupportedLanguages);
  const setSettingsRequestDone = useAppStore((state) => state.setSettingsRequestDone);
  const setLoading = useAppStore((state) => state.setLoading);

  // Favorites store actions
  const loadFavoritesFromStorage = useFavoritesStore((state) => state.loadFavoritesFromStorage);
  const rebuildFavoritesFromSections = useFavoritesStore((state) => state.rebuildFavoritesFromSections);

  // Jackpots store actions
  const startJackpotPolling = useJackpotsStore((state) => state.startPolling);
  const stopJackpotPolling = useJackpotsStore((state) => state.stopPolling);

  // Campaign store actions (includes tournament re-parsing)
  const { reParseTournamentsWithGames } = useCampaignActions();
  
  // Current players store actions
  const fetchCurrentPlayers = useFetchCurrentPlayers();

  // Start the automatic updates for Top Games Section
  useTopGames();

  // Enhanced query enablement to respect mountLobby flag like old lobby
  // mountLobby=true: Initialize immediately (default behavior)
  // mountLobby=false: Wait for shouldInitialize trigger (from display event)
  const queryEnabled =
    enabled && Boolean(settings.apiHost) && Boolean(balance.currency) && (settings.mountLobby || shouldInitialize);

  // Query for fetching settings
  const {
    data: settingsData,
    isLoading,
    isError,
    error,
    isSuccess,
    refetch,
  } = useQuery({
    queryKey: ['lobbySettings', settings.apiHost, balance.currency],
    queryFn: () => fetchSettings(settings.apiHost, balance.currency),
    enabled: queryEnabled && !isIdle,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, _error) => {
      // Only retry if it's not a development mode (mock data should always work)
      const isDev = !settings.apiHost ||
                   settings.apiHost.includes('example.com') ||
                   settings.apiHost.includes('localhost');
      return !isDev && failureCount < 3;
    },
    networkMode: 'online',
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  // Only fetch hot/cold stats if the settings response actually includes both
  // HOT_GAMES and COLD_GAMES source sections. Operators without the merged
  // section configured don't pay for an unused network round-trip.
  const hasHotColdSourceSections = Boolean(
    settingsData?.sections?.some((s) => s.name === SECTION_NAMES.HOT_GAMES) &&
    settingsData?.sections?.some((s) => s.name === SECTION_NAMES.COLD_GAMES)
  );

  const { data: hotColdStats } = useQuery({
    queryKey: ['hotColdStats', settings.apiHost],
    queryFn: () => fetchHotColdStats(settings.apiHost),
    enabled: queryEnabled && !isIdle && hasHotColdSourceSections,
    staleTime: 10 * 60 * 1000,
    retry: false,
    networkMode: 'online',
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  // Trending games come from a dedicated endpoint; the section comes from
  // settings for its config only. See useTrendingGames for the fetch gating.
  const { data: trendingGames } = useTrendingGames(
    settings.apiHost,
    settingsData?.sections,
    queryEnabled && !isIdle
  );

  // Update store when settings are successfully fetched
  useEffect(() => {
    // Settings effect debug disabled for production

    if (isSuccess && settingsData) {
      // Initialize translations BEFORE marking settings as done to prevent UI flash
      const initTranslations = async () => {
        // Load translations first with the actual language code from settings
        // Use settings.languageCode if available, otherwise fall back to first supported language
        const languageCode = settings.languageCode || settingsData.supportedLanguages?.[0] || 'en';

        await TranslationService.initialize(
          languageCode,
          settings.assetsHost || assetsConfig.assetsHost,
          settings.assetsHash || assetsConfig.assetsHash,
        );

        // Mark settings request as done AFTER translations are loaded
        setSettingsRequestDone(true);
      };

      // Start initialization
      initTranslations().catch((error) => {
        console.error('[useInitializeLobby] Failed to initialize translations:', error);
        // Still mark as done even if translations fail to prevent blocking UI
        setSettingsRequestDone(true);
      });

      // Update restrictions in settingsData in store
      updateSettings({ restrictions: settingsData.restrictions });

      // Extract and store feature flags from API response
      if (settingsData.featureFlags) {
        useFeatureFlagsStore.getState().setFlags(settingsData.featureFlags);
      }

      // Initialize last played games and create section
      const initializeLastPlayed = (currentSections: ISectionSettings[]): ISectionSettings[] => {
        try {
          // Find if LAST_PLAYED section exists from API
          const existingLastPlayedIndex = currentSections.findIndex(
            section => section.name === LAST_PLAYED_SECTION_NAME
          );

          // Only proceed if LAST_PLAYED section exists in API response
          if (existingLastPlayedIndex !== -1) {
            // Set player ID for user-specific storage
            if (settings.playerId) {
              lastPlayedService.setPlayerId(settings.playerId);
            }

            // Initialize last played games from localStorage
            lastPlayedService.initializeLastPlayedGames();

            // Add current game key if exists
            if (settings.gameKey) {
              lastPlayedService.addLastPlayedGame(settings.gameKey);
            }

            // Create LAST_PLAYED section with actual games
            const lastPlayedSection = lastPlayedService.createLastPlayedSection(
              settingsData.supportedGames,
              currentSections
            );

            if (lastPlayedSection) {
              // Replace existing LAST_PLAYED section with our populated one
              const sectionsWithLastPlayed = [...currentSections];
              sectionsWithLastPlayed[existingLastPlayedIndex] = lastPlayedSection;

              return sectionsWithLastPlayed;
            }

            // No last played games, remove empty LAST_PLAYED section
            return currentSections.filter(
              section => section.name !== LAST_PLAYED_SECTION_NAME
            );
          } else {
            // No LAST_PLAYED section from API, use sections as is
            return currentSections;
          }
        } catch (error) {
          console.warn('[useInitializeLobby] Failed to initialize last played games:', error);
          // Fall back to sections without modification
          return currentSections;
        }
      };

      const removeHotColdSections = (sections: ISectionSettings[]): ISectionSettings[] => {
        // Remove source sections (HOT_GAMES and COLD_GAMES) if they exist to prevent duplicates
        return sections.filter(
          (section) =>
            section.name !== SECTION_NAMES.HOT_GAMES &&
            section.name !== SECTION_NAMES.COLD_GAMES
        );
      };

      const initializeHotColdSection = (
        currentSections: ISectionSettings[],
        stats: IHotColdStatsResponse | undefined
      ): ISectionSettings[] => {
        try {
          // Find if HOT_GAMES and COLD_GAMES sections exists from API
          const existingHotSectionIndex = currentSections.findIndex(
            section => section.name === SECTION_NAMES.HOT_GAMES
          );

          const existingColdSectionIndex = currentSections.findIndex(
            section => section.name === SECTION_NAMES.COLD_GAMES
          );

          // Only proceed if HOT_GAMES and COLD_GAMES sections exists in API response
          if (existingHotSectionIndex === -1 || existingColdSectionIndex === -1) {
            // No HOT_GAMES and COLD_GAMES sections from API, use sections as is
            return removeHotColdSections(currentSections);
          }

          // Wait for the hot/cold stats endpoint before building the merged
          // section. If the query has not resolved yet (or returned nothing
          // usable), drop the source sections — the effect re-runs when the
          // stats arrive and the section is built then.
          if (!stats) {
            return removeHotColdSections(currentSections);
          }

          // Create HOT_AND_COLD section based on current sections data + stats endpoint
          const hotColdSection = createHotColdSection(currentSections, stats);

          if(hotColdSection) {
            // Replace existing HOT_GAMES and COLD_GAMES sections with our unified HOT_AND_COLD section
            const sectionsWithHotCold = [...currentSections];
            sectionsWithHotCold[existingHotSectionIndex] = hotColdSection;

            // Remove the old COLD_GAMES section
            return sectionsWithHotCold.filter((section) => section.name !== SECTION_NAMES.COLD_GAMES);
          }

          return removeHotColdSections(currentSections);
        } catch (error) {
          console.warn('[useInitializeLobby] Failed to initialize hot and cold games:', error);
          // Fall back to sections without modification
          return removeHotColdSections(currentSections);
        }
      };
      // Run the BE sections through the dynamic-section transforms. Each step
      // takes the full list, touches only its own section, and passes the rest
      // through. Build locally and set once (avoids stale closures and double updates).
      let modifiedSections = initializeLastPlayed(settingsData.sections);
      modifiedSections = initializeHotColdSection(modifiedSections, hotColdStats);
      modifiedSections = applyTrendingSection(modifiedSections, settingsData.supportedGames, trendingGames);

      // TODO_Rebate: Remove this once the BE is providing a REBATE section. This is a temporary workaround for QA purposes.
      // Inject a synthetic REBATE section when the feature flag is on and the
      // BE hasn't already provided one. The section shell is enough — the
      // campaign store drives the content; the section just needs to exist in
      // sections[] so Navigation and MultiViewLobby can surface it.
      const rebateFlagOn = useFeatureFlagsStore.getState().flags.enableRebateCampaign;
      const hasRebateSection = modifiedSections.some((s) => s.name === SECTION_NAMES.REBATE);
      if (rebateFlagOn && !hasRebateSection) {

        const res = (key: string) => buildPortalAssetUrl(`/campaigns/rebate/MultiLobby/${key}.webp`);
        const rebateSectionContext: IContextSectionSettings = {
          headerTitle: 'Winter Rebate Fiesta',
          prizeFund: '',
          prizeCurrency: '',
          showWelcomeModal: false,
          dateRange: ['2026-08-04T13:00:00Z', '2026-10-08T13:00:00Z'],
          templateInstance: {
            resources: {
              CampaignsMultiViewSoloSeparatedBackgroundLandscape: res('CampaignsMultiViewSoloSeparatedBackgroundLandscape'),
              CampaignsMultiViewSoloSeparatedBackgroundPortrait: res('CampaignsMultiViewSoloSeparatedBackgroundPortrait'),
              CampaignsMultiViewSoloTransparentImage: res('CampaignsMultiViewSoloTransparentImage'),
              CampaignsMultiViewSoloTransparentImageDesktop: res('CampaignsMultiViewSoloTransparentImageDesktop'),
            },
          },
        }

        const rebateSection: ISectionSettings = {
          name: SECTION_NAMES.REBATE,
          jackpotName: null,
          icon: 'rebate_icon',
          games: [],
          gridBlocks: 0,
          topLimit: undefined,
          minLimit: 1,
          banner: false,
          context: rebateSectionContext,
        };
        modifiedSections = [rebateSection, ...modifiedSections];
      }

      setSections(modifiedSections);

      // Telemetry: record dynamic-section active state for Sentry. Only
      // sections configured by the BE settings response are recorded; the
      // tracker is idempotent by name so refetches overwrite prior state.
      const beSections = settingsData.sections;
      const hotColdConfigured =
        beSections.some((s) => s.name === SECTION_NAMES.HOT_GAMES) &&
        beSections.some((s) => s.name === SECTION_NAMES.COLD_GAMES);
      if (hotColdConfigured) {
        dynamicSectionsTracker.recordSection(HOT_AND_COLD_SECTION_NAME, true);
      }
      if (beSections.some((s) => s.name === SECTION_NAMES.TOP_WIN_GAMES)) {
        dynamicSectionsTracker.recordSection(SECTION_NAMES.TOP_WIN_GAMES, true);
      }
      if (beSections.some((s) => s.name === SECTION_NAMES.TRENDING_GAMES)) {
        dynamicSectionsTracker.recordSection(SECTION_NAMES.TRENDING_GAMES, true);
      }

      // Update supported games for tournament parsing
      setSupportedGames(settingsData.supportedGames);

      // Re-parse tournaments now that games are available
      setTimeout(() => {
        reParseTournamentsWithGames();
      }, 100);

      // Update supported languages
      setSupportedLanguages(settingsData.supportedLanguages);

      // Load favorites and scroll positions from localStorage
      // Use setTimeout to prevent blocking the main thread
      setTimeout(() => {
        loadFavoritesFromStorage(settings.playerId);
        rebuildFavoritesFromSections(modifiedSections);
      }, 0);

      // Start jackpot polling after settings are loaded (always start, regardless of host)

      if (settings.apiHost) {
        // Set domain name in Sentry session
        sessionService.setDomainName(settings.apiHost);

        // Register jackpot polling (no host restrictions)
        registerProcess(
          'jackpot-polling',
          () => startJackpotPolling(settings.apiHost, balance.currency),
          () => stopJackpotPolling(),
        );
      }

      // Fetch current players data
      if (settings.apiHost && !settings.apiHost.includes('example.com')) {
        fetchCurrentPlayers(settings.apiHost).catch((error) => {
          console.warn('[useInitializeLobby] Failed to fetch current players:', error);
        });
      }

      // Initialize sound manager with assets configuration
      // Use setTimeout to prevent blocking the main thread
      setTimeout(() => {
        // Try settings first, then assetsConfig fallback
        const host = settings.assetsHost || assetsConfig.assetsHost;
        const hash = settings.assetsHash || assetsConfig.assetsHash || '';

        // For CDN environments, wait for assetsHash to be available
        // This prevents requests without the hash in the path
        if (host && host.includes('cdn') && (!hash || hash === '')) {
          // Skip initialization - will retry when assetsHash is available
          return;
        }

        if (host) {
          soundManager
            .initialize(host, hash)
            .then(() => {
              // Sound manager initialized successfully
            })
            .catch((error) => {
              console.warn('[useInitializeLobby] Sound manager initialization failed:', error);
              // Sound failure should not break the app, so we just log the warning
            });
        }
      }, 100); // Small delay to ensure UI is rendered first
    }
  }, [isSuccess, settingsData, hotColdStats, trendingGames, setSections, setSupportedGames, setSupportedLanguages, setSettingsRequestDone, loadFavoritesFromStorage, rebuildFavoritesFromSections, settings.playerId, settings.apiHost, settings.assetsHost, settings.assetsHash, balance.currency, balance.units, startJackpotPolling, stopJackpotPolling, assetsConfig.assetsHost, assetsConfig.assetsHash, reParseTournamentsWithGames, fetchCurrentPlayers, updateSettings, registerProcess, settings.gameKey, settings.languageCode]);

  // Update loading state
  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  // Handle errors and provide fallback
  useEffect(() => {
    if (isError && error) {
      // Settings request failed

      // For empty gameKey scenarios, provide fallback to prevent black screen
      const hasGameKeyParam = new URLSearchParams(window.location.search).has('gameKey');
      const gameKeyValue = new URLSearchParams(window.location.search).get('gameKey');
      const hasValidGameKey = gameKeyValue && gameKeyValue.trim() !== '';

      if (hasGameKeyParam && !hasValidGameKey) {
        // Providing fallback for empty gameKey scenario
        // Set a minimal sections array to unblock the UI
        setTimeout(() => {
          setSections([
            {
              name: 'ALL_GAMES',
              jackpotName: null,
              icon: 'all-games',
              games: [],
              gridBlocks: 12,
              banner: false,
            },
          ]);
          setSettingsRequestDone(true);
        }, 1000);
      }
    }
  }, [isError, error, setSections, setSettingsRequestDone]);

  // Cleanup jackpot polling on unmount
  useEffect(() => {
    return () => {
      // Cleaning up jackpot polling
      unregisterProcess('jackpot-polling');
    };
  }, [unregisterProcess]);

  return {
    // Query state
    isLoading,
    isError,
    isSuccess,
    error,

    // Data
    settingsData,

    // Actions
    refetch,

    // Helper flags
    canInitialize: Boolean(settings.apiHost) && Boolean(balance.currency),
  };
};
